import { LocalEnvironmentName } from "waapi/environments-repository/constants/environments.js";
import type { StoreState } from "waapi/girbal/Store/types.js";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { CHROME_EXTENSION_IDS, isProduction } from "../utils/constants.js";

export enum ExtensionStatus {
    UNKNOWN = "unknown",
    CONNECTING = "connecting",
    CONNECTED = "connected",
    CONNECTED_BUT_WITHOUT_API_TOKEN = "connected_but_without_api_token",
    FAILED_TO_CONNECT = "failed_to_connect",
    NOT_INSTALLED = "not_installed",
    RELOADING = "reloading",
    NO_EXTENSION_VERSION = "no_extension_version",
    TOO_MANY_VERSIONS = "too_many_versions",
}

export type UseExtensionInfosState = {
    extensionStatus: ExtensionStatus;
    extensionId: string;
    extensionVersion?: string;
    lastStorePush?: StoreState;
};

export const useExtensionInfos = create<UseExtensionInfosState>()(
    devtools(
        () => ({
            extensionStatus: ExtensionStatus.UNKNOWN,
            extensionId: CHROME_EXTENSION_IDS[LocalEnvironmentName.LOCAL],
            extensionVersion: undefined,
            lastStorePush: undefined,
        }),
        { name: "useExtensionInfos", store: "useExtensionInfos", enabled: !isProduction },
    ),
);

const CONNECTED_EXTENSION_STATUSES = [ExtensionStatus.CONNECTED, ExtensionStatus.CONNECTED_BUT_WITHOUT_API_TOKEN];

/** True when our Chrome extension has completed its handshake (vs merely running on a Chromium browser). */
export const isExtensionConnectedStatus = (extensionStatus: ExtensionStatus): boolean => CONNECTED_EXTENSION_STATUSES.includes(extensionStatus);

const UNREACHABLE_EXTENSION_STATUSES = [ExtensionStatus.NOT_INSTALLED, ExtensionStatus.FAILED_TO_CONNECT];

/**
 * True only on terminal connection failures (extension disabled/uninstalled). Transient states
 * (CONNECTING, RELOADING — ~6s during service-worker restarts) are NOT unreachable.
 */
export const isExtensionUnreachableStatus = (extensionStatus: ExtensionStatus): boolean => UNREACHABLE_EXTENSION_STATUSES.includes(extensionStatus);

/** Plain-function read of the connection state, for use outside React (routing in chromeMessages/tryRouteToCloud). */
export const isExtensionConnected = (): boolean => isExtensionConnectedStatus(useExtensionInfos.getState().extensionStatus);

/** Reactive selector version of {@link isExtensionConnected} for use inside components. */
export const useIsExtensionConnected = (): boolean => useExtensionInfos((state) => isExtensionConnectedStatus(state.extensionStatus));

export const setExtensionStatus = ({ extensionStatus }: { extensionStatus: ExtensionStatus }) => {
    useExtensionInfos.setState(
        (state) => ({
            extensionStatus,
            lastStorePush: isExtensionConnectedStatus(extensionStatus) ? state.lastStorePush : undefined,
        }),
        undefined,
        "setExtensionStatus",
    );
};

export const setExtensionInfos = (infos: UseExtensionInfosState) => {
    useExtensionInfos.setState(infos, undefined, "setExtensionInfos");
};

export const setLastStorePush = (lastStorePush: StoreState) => {
    useExtensionInfos.setState({ lastStorePush }, undefined, "setLastStorePush");
};
